import type { BackupVersion } from "@classroom/domain";
import type { BackupCipher, BackupConfiguration } from "../../../../../supabase/functions/_shared/backupTypes";
import type { SigningDevice } from "./controlClient";
import { createDeviceKeys } from "./cryptoV2";

export type BackupJob = {
  id: string;
  fileId: string;
  version: BackupVersion;
  cipher: BackupCipher;
  manifest: BackupCipher;
};
export type BackupDevice = {
  identity: SigningDevice & { wrapping: CryptoKey };
  keyId?: string;
  key?: CryptoKey;
  setup?: BackupConfiguration;
  job?: BackupJob;
  notice?: string;
  restoreIntent?: string;
  recoveryRoot?: {
    root: Omit<import("../../../../../supabase/functions/sync-control/model").Root, "recoveryVerified">;
    expectedGeneration: string | null;
  };
};
// Only device CryptoKeys, opaque ciphertext and non-classroom progress metadata
// are persisted. Recovery passwords and recovery signing secrets never are.
export class BackupVault {
  constructor(private readonly databaseName = "classroom-automatic-backup-v1") {}
  private async database() {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () => request.result.createObjectStore("devices");
      request.onsuccess = () => resolve(request.result);
      request.onerror = request.onblocked = () => reject(new Error("sync-device-storage-unavailable"));
    });
  }
  async read(accountId: string, workspaceId: string): Promise<BackupDevice | null> {
    const db = await this.database();
    try {
      return await new Promise((resolve, reject) => {
        const request = db
          .transaction("devices", "readonly")
          .objectStore("devices")
          .get(JSON.stringify([accountId, workspaceId]));
        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => reject(new Error("sync-device-storage-failed"));
      });
    } finally {
      db.close();
    }
  }
  async write(accountId: string, workspaceId: string, value: BackupDevice) {
    if (value.identity.accountId !== accountId || value.key?.extractable) throw new Error("sync-backup-key-invalid");
    const db = await this.database();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction("devices", "readwrite");
        tx.objectStore("devices").put(value, JSON.stringify([accountId, workspaceId]));
        tx.oncomplete = () => resolve();
        tx.onerror = tx.onabort = () => reject(new Error("sync-device-storage-failed"));
      });
    } finally {
      db.close();
    }
  }
  async identity(accountId: string, workspaceId: string, existing?: BackupDevice["identity"]) {
    // The caller holds an account/workspace Web Lock through read + creation.
    const saved = await this.read(accountId, workspaceId);
    if (saved) return saved;
    const identity = existing ?? {
      accountId,
      deviceId: crypto.randomUUID(),
      label: navigator.platform || "Browser",
      ...(await createDeviceKeys()),
    };
    const value = { identity };
    await this.write(accountId, workspaceId, value);
    return value;
  }
}
